import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, ScrollText, Shield, Heart, Eye, Stamp, FileKey, Gift, UserX, Briefcase,
  Plus, ChevronDown, CheckCircle2, Circle, Mail, XCircle, Edit3, Trash2,
  Phone, AtSign, MapPin, GripVertical, X, AlertTriangle, Search, Info
} from "lucide-react";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect from "../ui/SearchableSelect";

// ── Types ─────────────────────────────────────────────────────────────────────
interface Party {
  _id?: string;
  firstName: string;
  lastName: string;
  organization?: string;
  relationship?: string;
  email?: string;
  phone?: string;
  address?: string;
  role: string;
  subtype?: string;
  priorityOrder: number;
  status: string;
  appliesTo: string[];
  beneficiaryDetails?: any;
  witnessDetails?: any;
  notaryDetails?: any;
  guardianDetails?: any;
  advisorDetails?: any;
  disinheritedDetails?: any;
  notes?: string;
}

interface Props {
  matterId: string;
  caseData: any;
  onUpdate: () => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const ROLE_CONFIGS: Record<string, { label: string; plural: string; Icon: any; color: string; bgColor: string; badgeColor: string }> = {
  executor:     { label: "Executor",     plural: "Executors",            Icon: ScrollText, color: "text-amber-600",   bgColor: "bg-amber-50",   badgeColor: "bg-amber-100 text-amber-700" },
  trustee:      { label: "Trustee",      plural: "Trustees",             Icon: Shield,     color: "text-purple-600",  bgColor: "bg-purple-50",  badgeColor: "bg-purple-100 text-purple-700" },
  guardian:     { label: "Guardian",     plural: "Guardians",            Icon: Heart,      color: "text-pink-600",    bgColor: "bg-pink-50",    badgeColor: "bg-pink-100 text-pink-700" },
  witness:      { label: "Witness",      plural: "Witnesses",            Icon: Eye,        color: "text-blue-600",    bgColor: "bg-blue-50",    badgeColor: "bg-blue-100 text-blue-700" },
  notary:       { label: "Notary",       plural: "Notary",               Icon: Stamp,      color: "text-slate-600",   bgColor: "bg-slate-100",  badgeColor: "bg-slate-200 text-slate-700" },
  poa_agent:    { label: "POA Agent",    plural: "POA Agents",           Icon: FileKey,    color: "text-cyan-600",    bgColor: "bg-cyan-50",    badgeColor: "bg-cyan-100 text-cyan-700" },
  beneficiary:  { label: "Beneficiary",  plural: "Beneficiaries",        Icon: Gift,       color: "text-emerald-600", bgColor: "bg-emerald-50", badgeColor: "bg-emerald-100 text-emerald-700" },
  disinherited: { label: "Disinherited", plural: "Disinherited Parties", Icon: UserX,      color: "text-red-600",     bgColor: "bg-red-50",     badgeColor: "bg-red-100 text-red-700" },
  advisor:      { label: "Advisor",      plural: "Professional Advisors",Icon: Briefcase,  color: "text-indigo-600",  bgColor: "bg-indigo-50",  badgeColor: "bg-indigo-100 text-indigo-700" },
};

const STATUS_CONFIGS: Record<string, { label: string; Icon: any; color: string }> = {
  nominated: { label: "Nominated", Icon: Circle,       color: "bg-slate-100 text-slate-600" },
  informed:  { label: "Informed",  Icon: Mail,         color: "bg-blue-100 text-blue-700" },
  accepted:  { label: "Accepted",  Icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
  declined:  { label: "Declined",  Icon: XCircle,      color: "bg-red-100 text-red-700" },
};

const SUBTYPE_OPTIONS: Record<string, { value: string; label: string }[]> = {
  executor:    [{ value: "primary", label: "Primary" }, { value: "alternate", label: "Alternate" }, { value: "co", label: "Co-Executor" }],
  trustee:     [{ value: "initial", label: "Initial" }, { value: "successor", label: "Successor" }, { value: "co", label: "Co-Trustee" }, { value: "corporate", label: "Corporate/Institutional" }],
  guardian:    [{ value: "primary", label: "Primary" }, { value: "alternate", label: "Alternate" }],
  witness:     [{ value: "disinterested", label: "Disinterested" }, { value: "other", label: "Other" }],
  poa_agent:   [{ value: "financial_primary", label: "Financial — Primary" }, { value: "financial_successor", label: "Financial — Successor" }, { value: "healthcare_primary", label: "Healthcare — Primary" }, { value: "healthcare_successor", label: "Healthcare — Successor" }],
  beneficiary: [{ value: "specific", label: "Specific" }, { value: "residuary", label: "Residuary" }, { value: "contingent", label: "Contingent" }, { value: "class", label: "Class" }, { value: "charitable", label: "Charitable" }],
  advisor:     [{ value: "accountant", label: "Accountant" }, { value: "financial_advisor", label: "Financial Advisor" }, { value: "insurance_agent", label: "Insurance Agent" }, { value: "prior_attorney", label: "Prior Attorney" }],
};

const DOC_OPTIONS = [
  { value: "will", label: "Last Will & Testament" },
  { value: "trust", label: "Revocable Living Trust" },
  { value: "financial_poa", label: "Financial POA" },
  { value: "healthcare_poa", label: "Healthcare POA" },
];

const RELATIONSHIP_OPTIONS = [
  { value: "spouse", label: "Spouse" }, { value: "son", label: "Son" }, { value: "daughter", label: "Daughter" },
  { value: "parent", label: "Parent" }, { value: "sibling", label: "Sibling" }, { value: "grandchild", label: "Grandchild" },
  { value: "friend", label: "Friend" }, { value: "neighbor", label: "Neighbor" }, { value: "professional", label: "Professional" },
  { value: "charity", label: "Charity/Organization" }, { value: "other", label: "Other" },
];

const emptyParty: Party = {
  firstName: "", lastName: "", role: "", subtype: "", priorityOrder: 0, status: "nominated",
  appliesTo: [], email: "", phone: "", address: "", relationship: "", notes: "",
};

// ── Component ─────────────────────────────────────────────────────────────────
const EstatePartiesTab: React.FC<Props> = ({ matterId, caseData, onUpdate }) => {
  const parties: Party[] = caseData?.parties || [];
  const [activeFilter, setActiveFilter] = useState("all");
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(
    Object.keys(ROLE_CONFIGS).reduce((acc, k) => ({ ...acc, [k]: true }), {})
  );
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [form, setForm] = useState<Party>({ ...emptyParty });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [drawerParty, setDrawerParty] = useState<Party | null>(null);
  const [saving, setSaving] = useState(false);

  // Grouped parties
  const grouped = useMemo(() => {
    const g: Record<string, { party: Party; idx: number }[]> = {};
    Object.keys(ROLE_CONFIGS).forEach(r => { g[r] = []; });
    parties.forEach((p, i) => {
      if (g[p.role]) g[p.role].push({ party: p, idx: i });
    });
    return g;
  }, [parties]);

  const filteredRoles = activeFilter === "all"
    ? Object.keys(ROLE_CONFIGS)
    : [activeFilter];

  // Stats
  const statusCounts = useMemo(() => {
    const c = { nominated: 0, informed: 0, accepted: 0, declined: 0 };
    parties.forEach(p => { if (c[p.status as keyof typeof c] !== undefined) c[p.status as keyof typeof c]++; });
    return c;
  }, [parties]);

  const toggleSection = (role: string) => setExpandedSections(prev => ({ ...prev, [role]: !prev[role] }));

  const openAddModal = (presetRole?: string) => {
    setForm({ ...emptyParty, role: presetRole || "" });
    setEditingIdx(null);
    setModalStep(presetRole ? 1 : 1);
    setShowModal(true);
  };

  const openEditModal = (party: Party, idx: number) => {
    setForm({ ...party });
    setEditingIdx(idx);
    setModalStep(1);
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { estatesApi } = await import("../../services/api");
      if (editingIdx !== null) {
        await estatesApi.updateSubdoc(caseData._id, "parties", editingIdx, form);
      } else {
        await estatesApi.addSubdoc(caseData._id, "parties", form);
      }
      onUpdate();
      setShowModal(false);
    } catch (err) {
      console.error("Failed to save party:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (idx: number) => {
    try {
      const { estatesApi } = await import("../../services/api");
      await estatesApi.removeSubdoc(caseData._id, "parties", idx);
      onUpdate();
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error("Failed to delete party:", err);
    }
  };

  const getInitials = (f: string, l: string) => `${(f || "?")[0]}${(l || "?")[0]}`.toUpperCase();
  const formatLabel = (s: string) => s?.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") || "";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <Users className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Parties & Roles</h2>
              <p className="text-sm text-slate-500">{parties.length} {parties.length === 1 ? "party" : "parties"} across {Object.values(grouped).filter(g => g.length > 0).length} role types</p>
            </div>
          </div>
          <button onClick={() => openAddModal()} className="flex items-center gap-2 px-4 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] transition-colors">
            <Plus className="h-4 w-4" /> Add Party
          </button>
        </div>

        {/* Status summary cards */}
        {parties.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(STATUS_CONFIGS).map(([key, cfg]) => (
              <div key={key} className="bg-white rounded-xl border border-slate-100 p-3 text-center">
                <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide">{cfg.label}</p>
                <p className={`text-2xl font-bold mt-1 ${key === "accepted" ? "text-emerald-600" : key === "declined" ? "text-red-600" : key === "informed" ? "text-blue-600" : "text-slate-600"}`}>
                  {statusCounts[key as keyof typeof statusCounts]}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        <button
          onClick={() => setActiveFilter("all")}
          className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${activeFilter === "all" ? "bg-[#0F5132] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
        >
          All ({parties.length})
        </button>
        {Object.entries(ROLE_CONFIGS).map(([key, cfg]) => (
          <button
            key={key}
            onClick={() => setActiveFilter(key)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              activeFilter === key ? "bg-[#0F5132] text-white" : grouped[key].length > 0 ? "bg-slate-100 text-slate-600 hover:bg-slate-200" : "bg-slate-50 text-slate-400"
            }`}
          >
            {cfg.plural} ({grouped[key].length})
          </button>
        ))}
      </div>

      {/* Empty state */}
      {parties.length === 0 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <div className="h-16 w-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
            <Users className="h-8 w-8 text-amber-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No Parties Added Yet</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">Add the people involved in this estate matter — executors, trustees, beneficiaries, witnesses, and more.</p>
          <button onClick={() => openAddModal()} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] transition-colors">
            <Plus className="h-4 w-4" /> Add First Party
          </button>
        </motion.div>
      )}

      {/* Role category sections */}
      <AnimatePresence>
        {filteredRoles.map(role => {
          const cfg = ROLE_CONFIGS[role];
          const items = grouped[role];
          if (activeFilter !== "all" && items.length === 0) return null;
          const RoleIcon = cfg.Icon;
          const isOpen = expandedSections[role];
          const isDisinherited = role === "disinherited";

          return (
            <motion.div key={role} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className={`bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden ${isDisinherited && items.length > 0 ? "border-l-4 border-l-red-400" : ""}`}
            >
              <button onClick={() => toggleSection(role)} className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`h-9 w-9 rounded-xl ${cfg.bgColor} flex items-center justify-center`}>
                    <RoleIcon className={`h-4.5 w-4.5 ${cfg.color}`} />
                  </div>
                  <h3 className="font-semibold text-slate-800">{cfg.plural}</h3>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.badgeColor}`}>{items.length}</span>
                </div>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                    <div className="px-4 pb-4 space-y-2">
                      {items.length === 0 && (
                        <p className="text-sm text-slate-400 py-4 text-center">No {cfg.plural.toLowerCase()} added yet</p>
                      )}
                      {items.map(({ party: p, idx }, i) => {
                        const statusCfg = STATUS_CONFIGS[p.status] || STATUS_CONFIGS.nominated;
                        const StatusIcon = statusCfg.Icon;
                        return (
                          <motion.div key={idx} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                            className={`group flex items-start gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all cursor-pointer ${isDisinherited ? "bg-red-50/30" : "bg-slate-50/30"}`}
                            onClick={() => setDrawerParty(p)}
                          >
                            <div className={`h-10 w-10 rounded-full ${cfg.bgColor} flex items-center justify-center font-bold text-xs ${cfg.color} flex-shrink-0`}>
                              {getInitials(p.firstName, p.lastName)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-slate-800">{p.firstName} {p.lastName}</span>
                                {p.subtype && <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${cfg.badgeColor}`}>{formatLabel(p.subtype)}</span>}
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${statusCfg.color}`}>
                                  <StatusIcon className="h-3 w-3" /> {statusCfg.label}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 flex-wrap">
                                {p.relationship && <span>{formatLabel(p.relationship)}</span>}
                                {p.appliesTo?.length > 0 && p.appliesTo.map(d => (
                                  <span key={d} className="px-1.5 py-0.5 rounded text-[10px] bg-indigo-50 text-indigo-600 font-medium">{formatLabel(d)}</span>
                                ))}
                              </div>
                              {(p.email || p.phone) && (
                                <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400">
                                  {p.email && <span className="flex items-center gap-1"><AtSign className="h-3 w-3" />{p.email}</span>}
                                  {p.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{p.phone}</span>}
                                </div>
                              )}
                              {role === "beneficiary" && p.beneficiaryDetails?.specificBequest && (
                                <p className="text-xs text-slate-500 mt-1 italic">"{p.beneficiaryDetails.specificBequest}"</p>
                              )}
                              {role === "beneficiary" && p.beneficiaryDetails?.share && (
                                <p className="text-xs text-emerald-600 font-medium mt-0.5">Share: {p.beneficiaryDetails.share}</p>
                              )}
                              {role === "witness" && p.witnessDetails?.isDisinterested && (
                                <p className="flex items-center gap-1 text-xs text-emerald-600 mt-1"><CheckCircle2 className="h-3 w-3" /> Confirmed disinterested</p>
                              )}
                              {role === "notary" && p.notaryDetails?.commissionNumber && (
                                <p className="text-xs text-slate-500 mt-1">Commission #{p.notaryDetails.commissionNumber} · {p.notaryDetails.commissionState}</p>
                              )}
                              {role === "disinherited" && p.disinheritedDetails?.reason && (
                                <p className="text-xs text-red-600 mt-1 italic">"{p.disinheritedDetails.reason}"</p>
                              )}
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                              <button onClick={(e) => { e.stopPropagation(); openEditModal(p, idx); }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
                                <Edit3 className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(idx); }} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </motion.div>
                        );
                      })}
                      <button onClick={() => openAddModal(role)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-[#0F5132] py-2 px-3 transition-colors">
                        <Plus className="h-3.5 w-3.5" /> Add {cfg.label}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Witness count warning */}
              {role === "witness" && isOpen && items.length < 2 && items.length > 0 && (
                <div className="mx-4 mb-4 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  Most jurisdictions require at least 2 disinterested witnesses. Currently: {items.length}
                </div>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* ── Add/Edit Party Modal ─────────────────────────────────────────── */}
      <PremiumModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingIdx !== null ? "Edit Party" : "Add Party to Estate"}
        subtitle="Assign a person and their role in this estate matter"
        icon={<Users className="h-5 w-5" />}
        size="xl"
      >
        <div className="p-1">
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {[1, 2, 3].map(s => (
              <div key={s} className="flex items-center gap-2">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  modalStep === s ? "bg-[#0F5132] text-white" : modalStep > s ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"
                }`}>{modalStep > s ? <CheckCircle2 className="h-4 w-4" /> : s}</div>
                {s < 3 && <div className={`w-12 h-0.5 ${modalStep > s ? "bg-emerald-300" : "bg-slate-200"}`} />}
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 text-center mb-5">Step {modalStep} of 3: {modalStep === 1 ? "Identify the Person" : modalStep === 2 ? "Assign Role" : "Role Details"}</p>

          <AnimatePresence mode="wait">
            {/* Step 1: Person Info */}
            {modalStep === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
                    <input value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} placeholder="John" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
                    <input value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} placeholder="Doe" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Relationship to Testator</label>
                  <SearchableSelect options={RELATIONSHIP_OPTIONS} value={form.relationship || null} onChange={v => setForm({ ...form, relationship: v || "" })} placeholder="Select relationship" clearable />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="john@email.com" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                    <input value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(416) 555-1234" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                  <input value={form.address || ""} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="123 Main St, Toronto, ON" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Organization (if applicable)</label>
                  <input value={form.organization || ""} onChange={e => setForm({ ...form, organization: e.target.value })} placeholder="e.g., TD Wealth Management" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                </div>
              </motion.div>
            )}

            {/* Step 2: Role Assignment */}
            {modalStep === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Select Role *</label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(ROLE_CONFIGS).map(([key, cfg]) => {
                    const RIcon = cfg.Icon;
                    const isSelected = form.role === key;
                    return (
                      <button key={key} onClick={() => setForm({ ...form, role: key, subtype: "" })}
                        className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${
                          isSelected ? "border-emerald-500 bg-emerald-50/50 ring-2 ring-emerald-500/20" : "border-slate-200 hover:border-slate-300 hover:shadow-sm"
                        }`}
                      >
                        <RIcon className={`h-5 w-5 ${isSelected ? "text-emerald-600" : cfg.color}`} />
                        <span className={`text-xs font-medium ${isSelected ? "text-emerald-700" : "text-slate-600"}`}>{cfg.label}</span>
                      </button>
                    );
                  })}
                </div>
                {form.role && SUBTYPE_OPTIONS[form.role] && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Subtype</label>
                    <SearchableSelect options={SUBTYPE_OPTIONS[form.role]} value={form.subtype || null} onChange={v => setForm({ ...form, subtype: v || "" })} placeholder="Select subtype" clearable />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Priority Order</label>
                  <input type="number" min={0} value={form.priorityOrder} onChange={e => setForm({ ...form, priorityOrder: parseInt(e.target.value) || 0 })} className="w-24 rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Applies to Documents</label>
                  <div className="space-y-2">
                    {DOC_OPTIONS.map(d => (
                      <label key={d.value} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                        <input type="checkbox" checked={form.appliesTo?.includes(d.value)} onChange={e => {
                          const newApplies = e.target.checked ? [...(form.appliesTo || []), d.value] : (form.appliesTo || []).filter(v => v !== d.value);
                          setForm({ ...form, appliesTo: newApplies });
                        }} className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                        {d.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
                  <div className="flex gap-3">
                    {Object.entries(STATUS_CONFIGS).map(([key, cfg]) => (
                      <label key={key} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${form.status === key ? cfg.color : "bg-slate-50 text-slate-400 hover:bg-slate-100"}`}>
                        <input type="radio" name="status" value={key} checked={form.status === key} onChange={() => setForm({ ...form, status: key })} className="sr-only" />
                        <cfg.Icon className="h-3 w-3" /> {cfg.label}
                      </label>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Role-Specific Details */}
            {modalStep === 3 && (
              <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                {form.role === "beneficiary" && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Beneficiary Type</label>
                        <SearchableSelect options={[{ value: "specific", label: "Specific" }, { value: "residuary", label: "Residuary" }, { value: "contingent", label: "Contingent" }, { value: "class", label: "Class" }, { value: "charitable", label: "Charitable" }]}
                          value={form.beneficiaryDetails?.beneficiaryType || null} onChange={v => setForm({ ...form, beneficiaryDetails: { ...form.beneficiaryDetails, beneficiaryType: v } })} placeholder="Select type" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Share / Percentage</label>
                        <input value={form.beneficiaryDetails?.share || ""} onChange={e => setForm({ ...form, beneficiaryDetails: { ...form.beneficiaryDetails, share: e.target.value } })} placeholder="e.g., 50% of residuary" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Specific Bequest Description</label>
                      <textarea value={form.beneficiaryDetails?.specificBequest || ""} onChange={e => setForm({ ...form, beneficiaryDetails: { ...form.beneficiaryDetails, specificBequest: e.target.value } })} rows={2} placeholder="Describe the specific bequest..." className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                        <input type="checkbox" checked={form.beneficiaryDetails?.isMinor || false} onChange={e => setForm({ ...form, beneficiaryDetails: { ...form.beneficiaryDetails, isMinor: e.target.checked } })} className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                        Is a minor
                      </label>
                    </div>
                  </>
                )}
                {form.role === "witness" && (
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input type="checkbox" checked={form.witnessDetails?.isDisinterested ?? true} onChange={e => setForm({ ...form, witnessDetails: { isDisinterested: e.target.checked } })} className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                      Confirmed disinterested (no beneficial interest in the will)
                    </label>
                  </div>
                )}
                {form.role === "notary" && (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Commission Number</label>
                      <input value={form.notaryDetails?.commissionNumber || ""} onChange={e => setForm({ ...form, notaryDetails: { ...form.notaryDetails, commissionNumber: e.target.value } })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">State/Province</label>
                      <input value={form.notaryDetails?.commissionState || ""} onChange={e => setForm({ ...form, notaryDetails: { ...form.notaryDetails, commissionState: e.target.value } })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Expiration Date</label>
                      <input type="date" value={form.notaryDetails?.commissionExpiration ? new Date(form.notaryDetails.commissionExpiration).toISOString().split("T")[0] : ""} onChange={e => setForm({ ...form, notaryDetails: { ...form.notaryDetails, commissionExpiration: e.target.value } })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                    </div>
                  </div>
                )}
                {form.role === "guardian" && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Guardian Of</label>
                    <SearchableSelect options={[{ value: "person", label: "Person (Physical Care)" }, { value: "estate", label: "Estate (Financial)" }, { value: "both", label: "Both" }]}
                      value={form.guardianDetails?.guardianOf || "person"} onChange={v => setForm({ ...form, guardianDetails: { ...form.guardianDetails, guardianOf: v || "person" } })} />
                  </div>
                )}
                {form.role === "advisor" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Advisor Type</label>
                      <SearchableSelect options={SUBTYPE_OPTIONS.advisor} value={form.advisorDetails?.advisorType || null} onChange={v => setForm({ ...form, advisorDetails: { ...form.advisorDetails, advisorType: v } })} placeholder="Select type" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Firm / Company</label>
                      <input value={form.advisorDetails?.firmName || ""} onChange={e => setForm({ ...form, advisorDetails: { ...form.advisorDetails, firmName: e.target.value } })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                    </div>
                  </div>
                )}
                {form.role === "disinherited" && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Reason for Exclusion *</label>
                    <textarea value={form.disinheritedDetails?.reason || ""} onChange={e => setForm({ ...form, disinheritedDetails: { reason: e.target.value } })} rows={3} placeholder="Document the testator's stated reason for intentional exclusion..." className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                  <textarea value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Additional notes..." className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Modal footer */}
          <div className="flex justify-between mt-6 pt-4 border-t border-slate-100">
            <button onClick={() => modalStep > 1 ? setModalStep(modalStep - 1) : setShowModal(false)}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">
              {modalStep > 1 ? "Back" : "Cancel"}
            </button>
            <button
              onClick={() => modalStep < 3 ? setModalStep(modalStep + 1) : handleSave()}
              disabled={
                (modalStep === 1 && (!form.firstName || !form.lastName)) ||
                (modalStep === 2 && !form.role) ||
                saving
              }
              className="px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? "Saving..." : modalStep < 3 ? "Next" : editingIdx !== null ? "Save Changes" : "Add Party"}
            </button>
          </div>
        </div>
      </PremiumModal>

      {/* Delete confirmation */}
      <PremiumModal open={showDeleteConfirm !== null} onClose={() => setShowDeleteConfirm(null)} title="Remove Party" size="sm" icon={<AlertTriangle className="h-5 w-5" />}>
        <div className="p-4 text-center">
          <p className="text-sm text-slate-600 mb-4">
            Remove <strong>{showDeleteConfirm !== null ? `${parties[showDeleteConfirm]?.firstName} ${parties[showDeleteConfirm]?.lastName}` : ""}</strong> from this matter?
            This will remove the role assignment but not delete the contact.
          </p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => setShowDeleteConfirm(null)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">Cancel</button>
            <button onClick={() => showDeleteConfirm !== null && handleDelete(showDeleteConfirm)} className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700">Remove</button>
          </div>
        </div>
      </PremiumModal>

      {/* Party Detail Drawer */}
      <AnimatePresence>
        {drawerParty && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.3 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black z-40" onClick={() => setDrawerParty(null)} />
            <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-[400px] max-w-full bg-white shadow-2xl z-50 overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-slate-800">Party Detail</h3>
                  <button onClick={() => setDrawerParty(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X className="h-5 w-5" /></button>
                </div>
                <div className="flex items-center gap-3 mb-6">
                  <div className={`h-14 w-14 rounded-full ${ROLE_CONFIGS[drawerParty.role]?.bgColor || "bg-slate-100"} flex items-center justify-center font-bold text-lg ${ROLE_CONFIGS[drawerParty.role]?.color || "text-slate-600"}`}>
                    {getInitials(drawerParty.firstName, drawerParty.lastName)}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-lg">{drawerParty.firstName} {drawerParty.lastName}</p>
                    {drawerParty.relationship && <p className="text-sm text-slate-500">{formatLabel(drawerParty.relationship)} to Testator</p>}
                  </div>
                </div>
                {(drawerParty.email || drawerParty.phone || drawerParty.address) && (
                  <div className="space-y-2 mb-6 pb-6 border-b border-slate-100">
                    <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Contact</h4>
                    {drawerParty.email && <p className="flex items-center gap-2 text-sm text-slate-600"><AtSign className="h-4 w-4 text-slate-400" />{drawerParty.email}</p>}
                    {drawerParty.phone && <p className="flex items-center gap-2 text-sm text-slate-600"><Phone className="h-4 w-4 text-slate-400" />{drawerParty.phone}</p>}
                    {drawerParty.address && <p className="flex items-center gap-2 text-sm text-slate-600"><MapPin className="h-4 w-4 text-slate-400" />{drawerParty.address}</p>}
                  </div>
                )}
                <div className="space-y-3 mb-6">
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Role in This Matter</h4>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${ROLE_CONFIGS[drawerParty.role]?.badgeColor || "bg-slate-100 text-slate-600"}`}>
                      {drawerParty.subtype ? formatLabel(drawerParty.subtype) + " " : ""}{ROLE_CONFIGS[drawerParty.role]?.label || formatLabel(drawerParty.role)}
                    </span>
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_CONFIGS[drawerParty.status]?.color || "bg-slate-100 text-slate-600"}`}>
                      {STATUS_CONFIGS[drawerParty.status]?.label || drawerParty.status}
                    </span>
                  </div>
                  {drawerParty.appliesTo?.length > 0 && (
                    <div className="flex gap-1.5 flex-wrap">
                      {drawerParty.appliesTo.map(d => (
                        <span key={d} className="px-2 py-0.5 rounded text-[11px] bg-indigo-50 text-indigo-600 font-medium">{formatLabel(d)}</span>
                      ))}
                    </div>
                  )}
                </div>
                {drawerParty.notes && (
                  <div className="mb-6 pb-6 border-b border-slate-100">
                    <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Notes</h4>
                    <p className="text-sm text-slate-600">{drawerParty.notes}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EstatePartiesTab;
